import { useState } from "react";
import { motion } from "framer-motion";
import { Film, Camera, Share2, Check } from "lucide-react";

const TRAILER_YOUTUBE_ID = "lJKII2x1v54";
const TRAILER_TITLE = "SPECTRA // BLACKLINE — Official Trailer";

const stills = [
    {
        src: "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/p3rf5g5z_Screenshot%202026-07-10%20153156.png",
        caption: "Golden Hour · Salt Approach",
        span: "md:col-span-7 aspect-[16/9]",
    },
    {
        src: "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/epjb0ce1_Screenshot%202026-07-06%20102303.png",
        caption: "Rear View · Bonneville",
        span: "md:col-span-5 aspect-[4/3]",
    },
    {
        src: "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/m4ul0bg2_Screenshot%202026-07-08%20130109.png",
        caption: "HUD · Cockpit Wireframe",
        span: "md:col-span-5 aspect-[4/3]",
    },
    {
        src: "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/4uzv47us_Screenshot%202026-07-07%20142559.png",
        caption: "Control Deck · The Signal",
        span: "md:col-span-7 aspect-[16/9]",
    },
];

export default function Movies() {
    const [shareFeedback, setShareFeedback] = useState(null); // "shared" | "copied" | "error"

    const handleShare = async (e) => {
        e.stopPropagation();
        const shareData = {
            title: `Rane Dynamics — ${TRAILER_TITLE}`,
            text: "Watch the finished 3-minute trailer for SPECTRA // BLACKLINE — live now.",
            url: `https://www.youtube.com/watch?v=${TRAILER_YOUTUBE_ID}`,
        };
        try {
            if (navigator.share && typeof navigator.share === "function") {
                await navigator.share(shareData);
                setShareFeedback("shared");
            } else if (navigator.clipboard) {
                await navigator.clipboard.writeText(shareData.url);
                setShareFeedback("copied");
            } else {
                setShareFeedback("error");
            }
        } catch (err) {
            // AbortError = user cancelled the native share sheet, treat silently
            if (err && err.name !== "AbortError") setShareFeedback("error");
        }
        setTimeout(() => setShareFeedback(null), 2200);
    };

    return (
        <section
            id="movies"
            data-testid="movies-section"
            className="relative bg-[#0A0A0F] text-white py-24 md:py-40 grain overflow-hidden"
        >
            {/* cinematic gradient wash (accent, not dark-on-dark) */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-[#FF5500] opacity-[0.09] blur-[160px]" />
                <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#00E5FF] opacity-[0.06] blur-[140px]" />
            </div>

            <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-20">
                    <div className="md:col-span-7">
                        <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5500]">
                            003 // Cinema
                        </span>
                        <h2 className="mt-4 font-display font-black text-5xl md:text-7xl leading-[0.9] tracking-tight">
                            The Trilogy,<br />
                            <span className="italic font-light">on screen.</span>
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:pt-6">
                        <p className="text-lg text-zinc-300 leading-relaxed">
                            <span className="text-white font-semibold">{TRAILER_TITLE}</span>{" "}
                            is live. The finished 3-minute trailer brings the trilogy&apos;s
                            opening beats to the screen — Elias, the newly-birthed V5, and the
                            signal that broke containment.
                        </p>
                    </div>
                </div>

                {/* Main Player (YouTube embed) */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8 }}
                    className="relative rounded-3xl overflow-hidden border border-white/10 aspect-video bg-black"
                    data-testid="trailer-player"
                >
                    <iframe
                        src={`https://www.youtube.com/embed/${TRAILER_YOUTUBE_ID}?rel=0`}
                        title={TRAILER_TITLE}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        data-testid="trailer-iframe"
                        className="absolute inset-0 w-full h-full"
                    />

                    {/* Badge — non-interactive so it never blocks the player */}
                    <div className="absolute top-6 left-6 md:top-8 md:left-8 flex items-center gap-3 pointer-events-none">
                        <div className="inline-flex items-center gap-2 bg-black/60 backdrop-blur-xl border border-white/15 px-4 py-2 rounded-full">
                            <Film size={14} className="text-[#FF5500]" />
                            <span className="text-xs font-bold uppercase tracking-[0.25em]">
                                Official Trailer
                            </span>
                        </div>
                    </div>

                    {/* Status pill */}
                    <div className="absolute top-6 right-6 md:top-8 md:right-8 flex items-center gap-2 pointer-events-none">
                        <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5500] animate-pulse">
                            ● Live
                        </span>
                    </div>

                    {/* Share button */}
                    <div className="absolute bottom-6 right-6 md:bottom-8 md:right-8 z-10 flex items-center gap-2">
                        <button
                            data-testid="trailer-share-btn"
                            aria-label="Share the official trailer"
                            onClick={handleShare}
                            className={`inline-flex items-center gap-2 backdrop-blur-xl border px-3.5 py-2 rounded-full transition-colors ${
                                shareFeedback
                                    ? "bg-[#FF5500] border-[#FF5500] text-white"
                                    : "bg-black/60 hover:bg-white hover:text-zinc-950 border-white/15 text-white"
                            }`}
                        >
                            {shareFeedback ? <Check size={14} /> : <Share2 size={14} />}
                            <span className="text-[10px] font-bold uppercase tracking-[0.25em]">
                                {shareFeedback === "shared"
                                    ? "Shared"
                                    : shareFeedback === "copied"
                                      ? "Link copied"
                                      : shareFeedback === "error"
                                        ? "Try again"
                                        : "Share"}
                            </span>
                        </button>
                    </div>
                </motion.div>

                {/* Meta rail */}
                <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        {
                            label: "The Trailer",
                            value: "3:00",
                            desc: "The finished three-minute cut — live now on YouTube.",
                        },
                        {
                            label: "Watch",
                            value: "YouTube",
                            desc: "Stream and share the official trailer on the Rane Dynamics channel.",
                        },
                        {
                            label: "Universe",
                            value: "Book I → III",
                            desc: "Tight adaptation of the trilogy's opening beats.",
                        },
                    ].map((m) => (
                        <div
                            key={m.label}
                            data-testid={`trailer-meta-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="border border-white/10 rounded-2xl p-6 bg-white/[0.02] backdrop-blur"
                        >
                            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                {m.label}
                            </div>
                            <div className="mt-2 font-display font-black text-3xl">{m.value}</div>
                            <div className="mt-3 text-sm text-zinc-400">{m.desc}</div>
                        </div>
                    ))}
                </div>

                {/* Trailer Gallery */}
                <div
                    id="trailer-gallery"
                    data-testid="trailer-gallery"
                    className="mt-24 md:mt-32"
                >
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
                        <div>
                            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5500]">
                                First Look // Trailer Stills
                            </span>
                            <h3 className="mt-3 font-display font-black text-3xl md:text-5xl leading-[0.95] tracking-tight">
                                Frames from the film.
                            </h3>
                        </div>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400">
                            <Camera size={14} className="text-[#FF5500]" />
                            {stills.length + 1} stills · from the film
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
                        {stills.map((s, i) => (
                            <motion.figure
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: i * 0.08 }}
                                data-testid={`trailer-still-${i + 1}`}
                                className={`${s.span} group relative rounded-2xl overflow-hidden border border-white/10 bg-black`}
                            >
                                <img
                                    src={s.src}
                                    alt={s.caption}
                                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                <figcaption className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs uppercase tracking-[0.25em] text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                    <span>{s.caption}</span>
                                    <span className="text-[#FF5500]">#{String(i + 1).padStart(2, "0")}</span>
                                </figcaption>
                            </motion.figure>
                        ))}
                    </div>

                    <p className="mt-10 text-sm text-zinc-500 max-w-2xl">
                        Selected frames from {TRAILER_TITLE}. Watch the full 3-minute
                        cut above.
                    </p>
                </div>
            </div>
        </section>
    );
}
